import { ReliablePbxQueryService } from '@supply-mover-2-0/core';
import type {
	ReliablePbxDialOptionsModel,
	ReliablePbxDialResponseDto,
	ReliablePbxUserDialStatusDto,
} from '@supply-mover-2-0/shared';
import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useState,
} from 'react';

interface ReliablePbxContextValue {
	dialStatus: ReliablePbxUserDialStatusDto | null;
	isLoading: boolean;
	isDialing: boolean;
	error: string | null;
	canDial: boolean;
	dial: (
		phoneNumber: string,
		options?: ReliablePbxDialOptionsModel,
	) => Promise<ReliablePbxDialResponseDto>;
	refreshStatus: () => Promise<void>;
}

const ReliablePbxContext = createContext<ReliablePbxContextValue | undefined>(
	undefined,
);

export interface ReliablePbxProviderProps {
	children: React.ReactNode;
}

export const ReliablePbxProvider: React.FC<ReliablePbxProviderProps> = ({
	children,
}) => {
	const [dialStatus, setDialStatus] =
		useState<ReliablePbxUserDialStatusDto | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [isDialing, setIsDialing] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const refreshStatus = useCallback(async () => {
		try {
			setIsLoading(true);
			setError(null);
			const status = await ReliablePbxQueryService.getUserDialStatus(true);
			setDialStatus(status);
		} catch (err) {
			console.error('Failed to load Reliable PBX status:', err);
			setError('Failed to load Reliable PBX status');
			setDialStatus({
				connected: false,
				canDial: false,
				userExtension: null,
			});
		} finally {
			setIsLoading(false);
		}
	}, []);

	useEffect(() => {
		void refreshStatus();
	}, [refreshStatus]);

	const dial = useCallback(
		async (
			phoneNumber: string,
			options?: ReliablePbxDialOptionsModel,
		): Promise<ReliablePbxDialResponseDto> => {
			// The user's extension is admin-assigned; if it's missing they cannot
			// dial and must contact an administrator (no self-service setup).
			let currentStatus = dialStatus;
			let hasExtension =
				currentStatus?.userExtension != null &&
				currentStatus.userExtension.trim() !== '';

			if (currentStatus?.canDial !== true || !hasExtension) {
				try {
					currentStatus = await ReliablePbxQueryService.getUserDialStatus(true);
					setDialStatus(currentStatus);
					hasExtension =
						currentStatus.userExtension != null &&
						currentStatus.userExtension.trim() !== '';
				} catch {
					// Failed to refresh, continue with cached status
				}
			}

			if (currentStatus?.canDial !== true || !hasExtension) {
				return {
					success: false,
					error:
						'You need to have a Reliable PBX extension assigned to make calls. Please contact your administrator.',
				};
			}

			try {
				setIsDialing(true);

				return await ReliablePbxQueryService.dial(phoneNumber, options);
			} catch (err) {
				console.error('Failed to dial:', err);

				return {
					success: false,
					error: 'Failed to initiate call. Please try again.',
				};
			} finally {
				setIsDialing(false);
			}
		},
		[dialStatus],
	);

	const canDial = dialStatus?.canDial ?? false;

	return (
		<ReliablePbxContext.Provider
			value={{
				dialStatus,
				isLoading,
				isDialing,
				error,
				canDial,
				dial,
				refreshStatus,
			}}
		>
			{children}
		</ReliablePbxContext.Provider>
	);
};

/**
 * Hook to access Reliable PBX context.
 * Returns null if not in a ReliablePbxProvider.
 */
export const useReliablePbxSafe = (): ReliablePbxContextValue | null =>
	useContext(ReliablePbxContext) ?? null;
